<template>
  <v-card class="document-history" color="surface">
    <v-card-title class="d-flex align-center text-subtitle-1 py-3">
      History
      <v-spacer/>
      <v-btn :loading="loading" icon="mdi-refresh" size="small" variant="text" @click="load()"/>
    </v-card-title>

    <v-card-text class="pt-0">
      <v-progress-linear v-if="loading && !entries.length" color="primary" indeterminate/>

      <v-alert v-else-if="error" density="compact" type="error" variant="tonal">{{ error }}</v-alert>

      <p v-else-if="!entries.length" class="text-body-2 text-medium-emphasis mb-0">
        Nothing yet. Sends, customer views and downloads will show here.
      </p>

      <ul v-else class="document-history__list">
        <li v-for="entry in entries" :key="entry.id" class="document-history__item">
          <v-icon :color="iconFor(entry.event).color" class="document-history__icon" size="18">{{ iconFor(entry.event).icon }}</v-icon>
          <div class="document-history__body">
            <div class="text-body-2">
              <span v-if="entry.actor" class="font-weight-medium">{{ entry.actor }}</span>
              <span v-else class="font-weight-medium">Customer</span>
              {{ entry.actor ? entry.description : stripActorPhrase(entry.description) }}
              <span v-if="detailFor(entry)" class="text-medium-emphasis"> · {{ detailFor(entry) }}</span>
            </div>
            <div :title="absolute(entry.created_at)" class="text-caption text-medium-emphasis">{{ relative(entry.created_at) }}</div>
          </div>
        </li>
      </ul>
    </v-card-text>
  </v-card>
</template>

<script lang="ts" setup>
import {onMounted, ref, watch} from 'vue';
import axios from 'axios';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

type Entry = {
  id: number;
  event: string;
  description: string;
  actor: string | null;
  properties: Record<string, unknown>;
  created_at: string | null;
};

const props = defineProps<{ documentUuid: string; refreshKey?: number | string }>();

const entries = ref<Entry[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);

async function load(): Promise<void> {
  loading.value = true;
  error.value = null;
  try {
    const {data} = await axios.get(`/v1/order-documents/${props.documentUuid}/activity`);
    entries.value = data.activity ?? [];
  } catch (e) {
    console.error(e);
    error.value = "Couldn't load the history.";
  } finally {
    loading.value = false;
  }
}

function iconFor(event: string): { icon: string; color: string } {
  switch (event) {
    case 'sent':
      return {icon: 'mdi-send', color: 'primary'};
    case 'viewed':
      return {icon: 'mdi-eye-outline', color: 'success'};
    case 'downloaded':
      return {icon: 'mdi-download', color: 'primary'};
    case 'converted':
      return {icon: 'mdi-file-swap-outline', color: 'info'};
    case 'finalized':
      return {icon: 'mdi-check-decagram', color: 'success'};
    case 'deleted':
      return {icon: 'mdi-delete-outline', color: 'error'};
    default:
      return {icon: 'mdi-pencil-outline', color: 'grey'};
  }
}

function detailFor(entry: Entry): string {
  const parts: string[] = [];
  const channels = entry.properties?.channels as string[] | undefined;
  if (entry.properties?.email) parts.push(`email ${String(entry.properties.email)}`);
  if (entry.properties?.phone) parts.push(`text ${String(entry.properties.phone)}`);
  if (!parts.length && channels?.length) parts.push(channels.join(', '));
  return parts.join(', ');
}

/** "The customer opened the document" reads fine after the "Customer" label without its subject. */
function stripActorPhrase(description: string): string {
  return description.replace(/^The customer\s+/i, '');
}

function relative(value: string | null): string {
  return value ? dayjs(value).fromNow() : '';
}

function absolute(value: string | null): string {
  return value ? dayjs(value).format('MMM D, YYYY h:mm a') : '';
}

onMounted(load);
watch(() => props.refreshKey, () => { void load(); });

defineExpose({load});
</script>

<style scoped>
.document-history {
  width: 100%;
}

.document-history__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.document-history__item {
  display: flex;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.document-history__item:last-child {
  border-bottom: 0;
}

.document-history__icon {
  margin-top: 2px;
  flex: 0 0 auto;
}

.document-history__body {
  min-width: 0;
}
</style>
